import { useMutation } from '@tanstack/react-query';
import { useRef, useState, DragEvent, ChangeEvent, useEffect } from 'react';
import axios from 'axios';
import { KTSVG } from 'src/_metronic/helpers';

type ImageUploadType = {
  maxSize: number;
  allowTypes: string;
  itemTypeId: number;
  maxWidth: number;
  uploadedImage: (data: string) => void;
};
export default function ImageUpload(props: ImageUploadType) {
  const { maxSize, allowTypes, itemTypeId, uploadedImage, maxWidth } = props;
  const dragItem = useRef<any>();
  const dragOverItem = useRef<any>();
  const [inputKey, setInputKey] = useState<number>(0);
  const addImageRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<any>([]);
  const [tempImages, setTempImages] = useState([]);
  const [selectEdit, setSelectEdit] = useState<any>([]);
  const [beError, setBeError] = useState('');
  const [uploadStatus, setUploadStatus] = useState<boolean>(false);
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  const dragStart = (e: DragEvent<HTMLDivElement>, position: any) => {
    dragItem.current = position;
  };
  const dragEnter = (e: DragEvent<HTMLDivElement>, position: any) => {
    dragOverItem.current = position;
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    updateSelectedFiles(files);
  };
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    updateSelectedFiles(files);
    setInputKey((prevKey) => prevKey + 1);
  };
  const updateSelectedFiles = (filesToAdd: File[]) => {
    const updatedFiles = filesToAdd;

    setSelectedFiles(updatedFiles);

    // Create select edit state based on updated files
    const updatedSelectEdit = updatedFiles.map((file, index) => ({
      key: index,
      isEdit: false,
    }));

    setSelectEdit(updatedSelectEdit);
  };
  const drop = (e: any) => {
    const copyListItems = [...selectedFiles];
    const dragItemContent = copyListItems[dragItem.current];
    copyListItems.splice(dragItem.current, 1);
    copyListItems.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    setSelectedFiles(copyListItems);
  };
  const removeImage = (index: any) => {
    const temp = [...selectedFiles];
    const tempImage = [...tempImages];
    const tempSelectEdit = [...selectEdit];

    temp.splice(index, 1);
    tempSelectEdit.splice(index, 1);
    tempImage.splice(index, 1);

    setSelectedFiles(temp);
    setTempImages(tempImage);
    setSelectEdit(tempSelectEdit);
  };
  const REACT_APP_API_CMS_URL = `${process.env.REACT_APP_API_CMS_URL}`;
  const uploadImage = useMutation({
    mutationKey: ['upload-showcase-image' + selectedFiles],
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('templateItem', String(itemTypeId));
      return await axios.post(`${REACT_APP_API_CMS_URL}/mc/banner-upload`, formData);
    },
    onSuccess: (data) => {
      uploadedImage(data.data.name);
      setUploadStatus(true);
    },
    onError: (err: any) => {
      setBeError(err.response.data.message);
    },
  });
  const upload = async () => {
    for (const file of selectedFiles) {
      try {
        await uploadImage.mutateAsync(file);
      } catch (err) {
        return;
      }
    }
  };
  const fileExtension = '.' + selectedFiles[0]?.type.split('.').pop().toLowerCase();

  const formatBytes = (bytes: number) => {
    if (bytes < 1000) {
      return `${bytes} MB`;
    }

    const kb = bytes / 1000;

    if (kb < 1000) {
      return `${kb.toFixed(1).replace('.0', '')} KB`;
    }

    const mb = kb / 1000;

    if (mb < 1000) {
      return `${mb.toFixed(1).replace('.0', '')} MB`;
    }

    const gb = mb / 1000;

    if (gb < 1000) {
      return `${gb.toFixed(1).replace('.0', '')} GB`;
    }

    const tb = gb / 1000;

    return `${tb.toFixed(1).replace('.0', '')} TB`;
  };

  const formatImageBytes = (bytes: number) => {
    return bytes / (1024 * 1024);
  };

  const alertBox = (status?: boolean) => {
    if (!status && selectedFiles.length > 0 && !allowTypes.includes(fileExtension.replace('image/', ''))) {
      return <div className="alert alert-warning">Yükleyeceğiniz resim tipi belirtilen kriterlerin dışında.</div>;
    } else if (!status && formatImageBytes(Number(selectedFiles[0]?.size)) > maxSize) {
      return <div className="alert alert-warning">Yükleyeceğiniz resim boyutu belirtilen kriterlerin üzerinde.</div>;
    } else if (!status && beError) {
      return <div className="alert alert-danger">{beError}</div>;
    } else if (status) {
      return <div className="alert alert-success">Resim başarıyla yüklendi.</div>;
    }
  };

  useEffect(() => {
    if (selectedFiles.length > 0 && formatImageBytes(selectedFiles[0]?.size) < maxSize && allowTypes.includes(fileExtension.replace('image/', ''))) {
      upload();
    }
    setUploadStatus(false);
  }, [selectedFiles]);

  return (
    <div className="d-flex flex-column gap-4">
      <div
        className="border-2 border-gray-200 border-dashed h-200px p-3 d-flex align-items-center justify-content-center cursor-pointer"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => {
          selectedFiles.length === 0 && addImageRef.current?.click();
        }}
      >
        <input
          key={inputKey}
          accept={allowTypes}
          type="file"
          multiple={true}
          className="input-file cursor-pointer"
          ref={addImageRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {selectedFiles.length > 0 ? (
          selectedFiles.map((file: any, index: number) => (
            <div
              key={index}
              className={'card position-relative cursor-pointer'}
              draggable
              onDragEnter={(e) => dragEnter(e, index)}
              onDragStart={(e) => dragStart(e, index)}
              onDragEnd={drop}
            >
              <div className="position-absolute end-0 top-0 cursor-pointer" onClick={() => removeImage(index)}>
                <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-3" />
              </div>
              <div className={'d-flex flex-row h-180px justify-content-center align-items-center w-full overflow-hidden'}>
                <img src={file.src ? file.src : URL.createObjectURL(file)} className="img-cover" alt="" height={180} />
              </div>
            </div>
          ))
        ) : (
          <div className="d-flex flex-column justfiy-content-center align-items-center gap-1">
            <KTSVG path="/media/icons/duotone/Files/Pictures1.svg" className="svg-icon-5x" />
            <span className="fw-bold text-muted fs-7">Max Boyut: {formatBytes(maxSize)}</span>
            <span className="fw-bold text-muted fs-7">İzin verilen formatlar: {allowTypes}</span>
          </div>
        )}
      </div>
      {alertBox(uploadStatus)}
    </div>
  );
}
